/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';

import { LaunchesService } from '../services/launchesApi';
import type { PriceTableOverview } from '../types';

const EMPTY: PriceTableOverview = {
  current: null, history: [], seen_version: null, has_newer: false,
};

/**
 * The launch's price-table state: what is in force, what is archived, and
 * whether this user has opened the current one.
 *
 * `has_newer` is the whole reason this is a first-class screen — selling
 * from a superseded table is the most expensive mistake this area can
 * prevent.
 */
export function useLaunchPriceTables(agencyId: string, launchId: string) {
  const [data, setData] = useState<PriceTableOverview>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(() => {
    if (!agencyId || !launchId) return;
    LaunchesService.priceTables(agencyId, launchId)
      .then((overview) => setData(overview ?? EMPTY))
      .catch(() => setData(EMPTY))
      .finally(() => setIsLoading(false));
  }, [agencyId, launchId]);

  useEffect(() => { setIsLoading(true); load(); }, [load]);

  const markSeen = useCallback((version: number) => {
    LaunchesService.markPriceTableSeen(agencyId, launchId, version)
      .then(load)
      .catch(() => undefined);
  }, [agencyId, launchId, load]);

  return { ...data, isLoading, retry: load, markSeen };
}
